/*eslint-disable*/
import React, { useEffect, useState } from "react";
import EditableTextField from "../Input/EditableTextField";
import EditableDatePicker from "../Input/EditableDatePicker";

import ProductInterestForm from "../Forms/ProductInterestForm"; // Assuming the component is in the same folder

import CustomIconButton from "../Buttons/CustomIconButton";

import FastAPIClient from "../../client";
import config from "../../config";
import { HiOutlinePencil } from "react-icons/hi2";

import SavePopupMessage from "../ModalComponents/SavePopupMessage";
import ConfirmationModal from "../ModalComponents/ConfirmationModal";

import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";

import {
    ClientFormProps,
    Contract,
    ProductInterest,
    Attribute,
} from "../../types/client_form.t";

const client = new FastAPIClient(config);

const ClientForm: React.FC<ClientFormProps> = ({ client_id }) => {
    const [contracts, setContracts] = useState<Contract[]>([]);

    const [isEditing, setIsEditing] = useState<boolean>(false); // Add state for edit mode
    const [message, setMessage] = useState<{
        text: string;
        type: "success" | "error";
    } | null>(null);

    const [isLoading, setIsLoading] = useState(true);

    // Handle adding a new contract (if needed)
    const handleAddContract = () => {
        setIsEditing(true);
        setContracts([
            ...contracts,
            { name: "", end_date: "", contract_id: "" },
        ]);
    };

    // Handle changes to contract input fields
    const handleContractInputChange = (
        index: number,
        field: keyof Contract,
        value: string
    ) => {
        const updatedContracts = [...contracts];
        updatedContracts[index][field] = value;
        setContracts(updatedContracts);
    };

    const [originalProductInterests, setOriginalProductInterests] = useState<
        ProductInterest[]
    >([]);

    const handleRemoveContract = (index: number) => {
        // Remove the contract at the specified index
        const updatedContracts = [...contracts];
        updatedContracts.splice(index, 1);
        setContracts(updatedContracts); // Update the contracts state
    };

    // Toggle editing mode
    const toggleEdit = () => {
        setIsEditing(!isEditing);
    };

    const [productInterests, setProductInterests] = useState<ProductInterest[]>(
        []
    );
    const [categoryAttributes, setCategoryAttributes] = useState<
        Record<number, Attribute[]>
    >({});

    // Simulate fetching product interests for a client
    const fetchInterests = (
        categories: {
            category_id: number;
            name: string;
            attributes: {
                attribute_id: number;
                attribute_name: string;
                attribute_type: string;
                category_id: number;
                attribute_value?: string;
            }[];
        }[]
    ) => {
        return new Promise<ProductInterest[]>((resolve) => {
            // Simulating data if the client already has interests

            // If no existing interests, create default ones
            resolve(
                categories.map((cat) => ({
                    category_id: cat.category_id,
                    name: cat.name,
                    is_interested: undefined,
                    volume_unit: undefined,
                    volume: undefined,
                    // Initialize attributes with their name and type
                    attributes: cat.attributes.reduce((acc, attr) => {
                        acc[attr.attribute_id] = {
                            name: attr.attribute_name, // Store the name of the attribute
                            type: attr.attribute_type, // Store the type of the attribute
                            value: attr.attribute_value ?? "", // Store the type of the attribute
                        };
                        return acc;
                    }, {} as Record<number, { name: string; type: string; value: string }>),
                }))
            );
        });
    };

    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true); // Start loading

        try {
            await client.updateClientInterest(client_id, productInterests);

            await client.updateClientLeadContracts(client_id, contracts);

            setIsEditing(false);

            // Show success message
            setMessage({
                text: "Données enregistrées avec succès !",
                type: "success",
            });

            // Hide message after 3 seconds
            setTimeout(() => setMessage(null), 3000);
        } catch (error) {
            console.error("Error saving data:", error);

            // Show error message
            setMessage({
                text: "Échec de l'enregistrement des données. Veuillez réessayer.",
                type: "error",
            });

            // Hide message after 3 seconds
            setTimeout(() => setMessage(null), 3000);
        }

        setIsSaving(false); // Stop loading after API response
    };

    useEffect(() => {
        async function loadData() {
            try {
                const categories = await client.getLeadFormLayout();
                const lead_form = await client.getClientLeadForm(client_id);
                const leadContracts = await client.getClientLeadContracts(
                    client_id
                );

                setContracts(leadContracts); // Set the fetched contracts in the state

                const interests = await fetchInterests(categories);

                setCategoryAttributes(categories);

                // If lead_form is empty, use interests instead
                if (!lead_form || lead_form.length === 0) {
                    setProductInterests(interests);
                    setOriginalProductInterests(interests);
                } else {
                    setProductInterests(lead_form);
                    setOriginalProductInterests(lead_form);
                }
            } catch (error) {
                console.error("Error loading data:", error);
            } finally {
                setIsLoading(false);
            }
        }
        loadData();
    }, []);

    const handleCancel = () => {
        // Reset product interests
        setProductInterests(originalProductInterests);

        // Remove contracts with an empty name
        setContracts((prevContracts) =>
            prevContracts.filter((contract) => contract.name.trim() !== "")
        );

        // Exit edit mode
        setIsEditing(false);
    };

    // Update product interest state
    const handleProductInterestChange = (
        index: number,
        field: string,
        value: any
    ) => {
        setProductInterests((prev) =>
            prev.map((interest, i) =>
                i === index ? { ...interest, [field]: value } : interest
            )
        );
    };

    const handleEditMode = () => {
        setIsEditing(true);
    };

    return (
        <div>
            {/* Contract with Distributor */}
            {isLoading ? (
                <div className="mt-4">
                    <Box>
                        {[...Array(6)].map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rounded"
                                height={40}
                                className="mb-4"
                            />
                        ))}
                    </Box>
                </div>
            ) : (
                <>
                    <div className="flex w-full flex-row items-center justify-center w-full">
                        <div
                            className={`flex ${
                                isEditing ? "w-full" : "w-5/6"
                            } font-semibold text-base text-left md:text-lg hidden md:block`}
                        >
                            Contrats & produits
                        </div>
                        <div className="flex w-5/6 font-semibold text-base text-left md:text-lg block md:hidden "></div>
                        {isEditing ? (
                            <ConfirmationModal
                                onCancel={handleCancel}
                                onSave={handleSave}
                                message="Voulez-vous enregistrer les modifications ?"
                                isEditing={true}
                                isSaving={isSaving}
                            />
                        ) : (
                            <div className="flex w-1/6 hidden md:block">
                                <CustomIconButton
                                    onClick={toggleEdit}
                                    icon={<HiOutlinePencil />}
                                    sx={{ fontSize: "18px" }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-row space-x-4 mt-4 mb-4 w-full items-center">
                        <div className=" w-2/3 text-left font-semibold">
                            Est-ce que le prospect a des contrats en cours ?
                        </div>
                        <div className="flex w-1/3 space-x-4 text-xxs">
                            <button
                                className={`p-1 w-10 text-black rounded-full ${
                                    contracts.length > 0
                                        ? "bg-gold"
                                        : "bg-gray-300"
                                }`}
                                onClick={() => {
                                    setContracts([
                                        {
                                            name: "",
                                            end_date: "",
                                            contract_id: "",
                                        },
                                    ]); // Add a new contract
                                    setIsEditing(true); // Enable editing mode
                                }}
                            >
                                Oui
                            </button>
                            <button
                                className={`p-1 w-10 rounded-full ${
                                    contracts.length === 0
                                        ? "bg-rose-500 text-white"
                                        : "bg-gray-300 text-black"
                                }`}
                                onClick={() => {
                                    setContracts([]); // Remove all contracts
                                    setIsEditing(true); // Enable editing mode
                                }}
                            >
                                Non
                            </button>
                        </div>
                    </div>

                    <div className="w-full text-left">
                        {contracts.map((contract, index) => (
                            <div
                                key={index}
                                className="flex flex-col border-b border-gray-200"
                            >
                                <div className="w-full flex flex-row">
                                    <div className="w-4/5">
                                        <EditableTextField
                                            label="Nom du contrat"
                                            name="name"
                                            value={contract.name}
                                            isEditing={isEditing} // Pass isEditing state
                                            error={false}
                                            helperText=""
                                            onChange={(e) =>
                                                handleContractInputChange(
                                                    index,
                                                    "name",
                                                    e.target.value
                                                )
                                            }
                                            fontSize="16px"
                                        />
                                    </div>
                                    {isEditing && (
                                        <button
                                            onClick={() =>
                                                handleRemoveContract(index)
                                            }
                                            className="mt-2 text-sm font-bold text-red-600 justify-center w-1/5"
                                        >
                                            x
                                        </button>
                                    )}
                                </div>

                                <div className="w-full flex flex-row justify-start mt-2 mb-2">
                                    <EditableTextField
                                        label="Produits famille travaille"
                                        name="description"
                                        value={contract.description}
                                        isEditing={isEditing} // Pass isEditing state
                                        error={false}
                                        helperText=""
                                        onChange={(e) =>
                                            handleContractInputChange(
                                                index,
                                                "description",
                                                e.target.value
                                            )
                                        }
                                        fontSize="16px"
                                    />
                                    <div className="ml-2">
                                        <EditableDatePicker
                                            label={"Date de fin"}
                                            value={contract.end_date}
                                            isEditing={isEditing} // Pass isEditing state
                                            onChange={(newDate) =>
                                                handleContractInputChange(
                                                    index,
                                                    "end_date",
                                                    newDate ?? ""
                                                )
                                            }
                                        />
                                    </div>
                                </div>

                                {/* Remove contract button */}
                            </div>
                        ))}

                        <button
                            onClick={handleAddContract}
                            className="mt-2 text-sm font-bold"
                        >
                            + Ajouter un contrat
                        </button>
                    </div>

                    {/* Product Family Interest */}
                    <div className="w-full text-left mt-4 border-t border-gray-200 pt-2 md:mb-0 md:pb-0 mb-20 pb-20">
                        <div className="flex flex-row items-center justify-center">
                            <div className="font-semibold text-base text-left w-full">
                                Potentiel par famille produit
                            </div>

                            <CustomIconButton
                                onClick={toggleEdit}
                                icon={<HiOutlinePencil />}
                                sx={{ fontSize: "18px" }}
                            />
                        </div>
                        {productInterests.map((interest, index) => (
                            <ProductInterestForm
                                key={interest.category_id}
                                index={index}
                                interest={interest}
                                attributes={
                                    categoryAttributes[interest.category_id] ||
                                    []
                                }
                                handleProductInterestChange={
                                    handleProductInterestChange
                                }
                                isEditing={isEditing}
                                setIsEditing={handleEditMode} // Pass the function
                            />
                        ))}
                    </div>
                </>
            )}

            {/* Display the popup message */}
            {message && (
                <SavePopupMessage
                    popupType={message.type}
                    popupMessage={message.text}
                />
            )}
        </div>
    );
};

export default ClientForm;
